import * as fs from "mz/fs";
import * as semver from "semver";
import * as path from "path";
import * as npmUtil from "@pagedip/util-npm";
import * as yaml from "js-yaml";
import { coerceArray, isNonEmptyString } from "./util";
import log from "@pagedip/util-logger";

export namespace Release {
  export type Type = "skip" | "patch" | "minor" | "major";
  export type Access = "public" | "restricted";
}

export class Release {
  filePath: string;
  _type?: Release.Type;
  _reason?: string;
  breaking: string[];
  features: string[];
  fixes: string[];
  chores: string[];
  version?: string;
  prerelease?: boolean | string;
  gitRef?: string;
  distTag?: string;
  access?: Release.Access;

  static types: Release.Type[] = ["skip", "patch", "minor", "major"];
  static isType(t: any): t is Release.Type {
    return Release.types.includes(t);
  }
  static getMaxType(...types: Array<Release.Type | undefined>) {
    let max: number | undefined;
    for (const t of types) {
      const ti = t != null ? Release.types.indexOf(t) : undefined;
      max = max == null ? ti : ti == null ? max : Math.max(max, ti);
    }
    return max != null ? Release.types[max] : undefined;
  }

  static access: Release.Access[] = ["restricted", "public"];
  static isAccess(a: any): a is Release.Access {
    return Release.access.includes(a);
  }

  static getConfigPath(dir?: string) {
    return path.resolve(dir || ".", "RELEASE.yml");
  }

  static async findFiles(dir?: string) {
    const dirpath = path.resolve(dir || ".");

    return (await fs.readdir(dirpath))
      .filter((filename) => {
        const extname = path.extname(filename);
        const basename = path.basename(filename, extname);

        return (
          (extname.toLowerCase() === ".yml" || extname.toLowerCase() === ".yaml") && /^RELEASE(\..+)?$/.test(basename)
        );
      })
      .sort()
      .map((filename) => {
        return path.join(dirpath, filename);
      });
  }

  static async load(dir?: string) {
    dir = path.resolve(dir || ".");
    const releases: Release[] = [];
    const files = await Release.findFiles(dir);

    for (const filePath of files) {
      const releaseYaml = await fs.readFile(filePath, "utf-8");
      const conf = yaml.safeLoad(releaseYaml);
      releases.push(new Release(filePath, conf));
    }

    const result = Release.merge(...releases);
    result.filePath = Release.getConfigPath(dir);
    return result;
  }

  static merge(...releases: Release[]) {
    let filePath: string | undefined;
    const result = new Release();

    for (const release of releases) {
      filePath = release.filePath;
      result._type = Release.getMaxType(release._type, result._type);
      if (release.access != null) result.access = release.access;
      if (release.version != null) result.version = release.version;
      if (release.gitRef != null) result.gitRef = release.gitRef;
      if (release.distTag != null) result.distTag = release.distTag;
      if (release.prerelease != null) result.prerelease = release.prerelease;
      result.breaking = result.breaking.concat(release.breaking);
      result.features = result.features.concat(release.features);
      result.fixes = result.fixes.concat(release.fixes);
      result.chores = result.chores.concat(release.chores);
    }

    if (filePath) result.filePath = filePath;
    return result;
  }

  constructor(filePath?: string, props?: Record<string, unknown>) {
    if (props == null) props = {};

    this.filePath = path.resolve(filePath || ".");

    if (Release.isType(props.type)) this._type = props.type;
    if (Release.isAccess(props.access)) this.access = props.access;

    if (isNonEmptyString(props.version)) this.version = props.version;
    if (isNonEmptyString(props.gitRef)) this.gitRef = props.gitRef;
    if (isNonEmptyString(props.distTag)) this.distTag = props.distTag;

    if (isNonEmptyString(props.prerelease) || typeof props.prerelease === "boolean") {
      this.prerelease = props.prerelease;
    }

    this.breaking = coerceArray(props.breaking, isNonEmptyString);
    this.features = coerceArray(props.features, isNonEmptyString);
    this.fixes = coerceArray(props.fixes, isNonEmptyString);
    this.chores = coerceArray(props.chores, isNonEmptyString);
  }

  static isRelease(r: any): r is Release {
    return Boolean(r && r.__autorelease_release__ === true);
  }

  get __autorelease_release__() {
    return true;
  }

  _changesLevel() {
    return this.breaking.length
      ? 3 // major
      : this.features.length
      ? 2 // minor
      : this.fixes.length
      ? 1
      : 0; // patch or skip
  }

  _explicitLevel() {
    return Release.types.indexOf(this._type || "skip");
  }

  hasDeclaredChanges() {
    return this.breaking.length > 0 || this.features.length > 0 || this.fixes.length > 0 || this.chores.length > 0;
  }

  hasExplicitType() {
    return this._type != null;
  }

  get type() {
    return Release.types[Math.max(this._explicitLevel(), this._changesLevel())];
  }

  set type(t) {
    this._type = t;
  }

  get reason() {
    if (this._reason != null) return this._reason;

    const minLevel = this._changesLevel();
    const declaredLevel = this._explicitLevel();

    return declaredLevel > minLevel ? "declared release type" : "declared changes";
  }

  set reason(r) {
    this._reason = r;
  }

  reset() {
    const { version, prerelease, distTag, access } = this;
    return new Release(this.filePath, { version, prerelease, distTag, access });
  }

  toYaml() {
    const obj: any = {};
    let count = 0;

    const keys: Array<keyof Release> = [
      "breaking",
      "features",
      "fixes",
      "chores",
      "version",
      "prerelease",
      "gitRef",
      "distTag",
      "access",
      "type",
    ];

    for (const key of keys) {
      const val = this[key];
      if (val == null) continue;
      if (key === "type" && val === "skip") continue;
      if (Array.isArray(val) && !val.length) continue;
      obj[key] = val;
      count++;
    }

    let out = count ? yaml.safeDump(obj, { skipInvalid: true }).trim() : "";

    for (const key of ["breaking", "features", "fixes", "chores"]) {
      if (obj[key] == null) {
        out += "\n" + key + ":";
      }
    }

    return out.trim() + "\n";
  }

  async write(options?: { basedir?: string; dryrun?: boolean; debug?: (msg: string) => any }) {
    options = options || {};
    const output = this.toYaml();
    const filePath = path.resolve(options.basedir || ".", this.filePath);
    if (!options.dryrun) await fs.writeFile(this.filePath, output);
    if (options.debug) {
      const relPath = options.basedir ? path.relative(options.basedir, filePath) : filePath;
      options.debug(`wrote release config to ${log.color.blue(relPath)}`);
    }
  }
}

export interface VersionChange {
  type: Release.Type;
  anchor?: string;
  pivot?: string;
  next?: string;
  prerelease?: string;
}

export function resolveVersionChange({
  info,
  previous,
  type,
  prerelease,
}: {
  type: Release.Type;
  previous?: string;
  info?: npmUtil.PackageInfo;
  prerelease?: boolean | string;
}) {
  if (!type) {
    throw new Error("Expecting a release type to resolve version.");
  }

  const result: VersionChange = { type };
  const anchor = new semver.SemVer(previous || "1.0.0");
  const root = [anchor.major, anchor.minor, anchor.patch].join(".");
  const prid = typeof prerelease === "string" ? prerelease : undefined;
  let pivot: string | null = null;
  let next: string | null = null;

  if (previous) result.anchor = anchor.version;
  if (prerelease) result.prerelease = prid;
  if (type === "skip") return result;

  if (info) {
    // grab versions from time instead of versions so we include unpublished
    const versions = Object.keys(info.time).filter((v) => semver.valid(v));

    // create a semver range to find the highest version already released
    let range;
    if (type === "patch") {
      range = "~" + root;
    } else if (type === "minor") {
      range = "^" + [anchor.major, anchor.minor, "0"].join(".");
    } else if (type === "major") {
      range = ">=" + [anchor.major, "0", "0"].join(".");
    } else {
      throw new Error(`Unexpected release type '${type}'`);
    }

    // find highest version released
    const highest = (pivot = semver.maxSatisfying(versions, range));

    // handle prerelease
    if (prerelease) {
      // create a version range to find existing prereleases
      let prstart: string | null;
      let prend: string | null;

      // if a version has been released in the range, we need a full bump up to
      // the next version since this version has been burned
      if (highest) {
        const prtype = ("pre" + type) as semver.ReleaseType;
        prstart = semver.inc(highest, prtype, undefined, prid);
        prend = semver.inc(highest, type);
      }

      // otherwise a "real" release hasn't happened yet for the current version
      // and we need the highest prerelease for the current version
      else {
        prstart = root + "-" + prid + ".0";
        prend = semver.inc(root, type);
      }

      // find existing prerelease
      const prrange = `>=${prstart} <${prend}`;
      const prver = semver.maxSatisfying(versions, prrange);

      // bump according to what we find
      if (prver) {
        pivot = prver;
        next = semver.inc(prver, "prerelease", undefined, prid);
      } else {
        next = prstart;
      }
    } else {
      // if a version has been release in the range, we need a full bump up to
      // the next version since this version has been burned
      if (highest) next = semver.inc(highest, type, undefined, prid);
      // otherwise a "real" release hasn't happened yet for the current version
      else next = root;
    }
  } else {
    next = root;
    if (prerelease) next += prid + ".0";
  }

  if (next) result.next = next;
  if (pivot) result.pivot = pivot;

  return result;
}
